import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import CountUp from "react-countup";
import Header from "../../../../components/Header";
import PageBanner from "../../../../components/PageBanner";
import Footer from "../../../../components/Footer";
import GalleryTabs from "./GalleryTabs";
import { MoreToggle } from "../../../../components/ShowMore";
import "../../../../pages/home/homeRedesign.css"; // shared primitives: .btn-ed, .eyebrow-ed, .heading-ed
import "./alumniNew.css";
import { mediaUrl } from "../../../../services/assetUrl";
import alumniFallback from "../../../../data/alumni/alumni.json";

// Normalize the image URLs inside gallery events (meets / talks).
const normalizeEvents = (events = []) =>
  events.map((e) => ({
    ...e,
    images: (e.images || []).map(mediaUrl),
    imagesHidden: (e.imagesHidden || []).map(mediaUrl),
  }));

const EASE = [0.23, 1, 0.32, 1];
const containerV = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const itemV = {
  hidden: { opacity: 0, transform: "translateY(22px)" },
  show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.45, ease: EASE } },
};

// scroll-reveal wrapper (framer whileInView)
const Rise = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, transform: "translateY(26px)" }}
    whileInView={{ opacity: 1, transform: "translateY(0px)" }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

const SectionHead = ({ eyebrow, heading, note, center = true }) => (
  <div className={`alm-head${center ? " alm-head--center" : ""}`}>
    <span className="eyebrow-ed">{eyebrow}</span>
    <h2 className="heading-ed">{heading}</h2>
    {note && <p className="alm-head__note">{note}</p>}
  </div>
);

/* --- Leadership icons (stroke, currentColor) --- */
const svgProps = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
const LEADER_ICONS = {
  "working president": (<svg {...svgProps}><circle cx="12" cy="8" r="5" /><path d="M8.2 12.6 7 22l5-3 5 3-1.2-9.4" /><path d="m12 6 .9 1.8 2 .3-1.45 1.4.35 2L12 10.6 10.2 11.5l.35-2L9.1 8.1l2-.3Z" /></svg>),
  secretary: (<svg {...svgProps}><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>),
  "joint secretary": (<svg {...svgProps}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>),
  treasurer: (<svg {...svgProps}><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10h18" /><path d="M16.5 14h1.5" /></svg>),
};
const COORD_ICONS = {
  ECE: (<svg {...svgProps}><rect x="6" y="6" width="12" height="12" rx="2" /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" /></svg>),
  CSE: (<svg {...svgProps}><path d="m16 18 6-6-6-6" /><path d="m8 6-6 6 6 6" /></svg>),
  ISE: (<svg {...svgProps}><rect x="9" y="3" width="6" height="5" rx="1" /><rect x="2.5" y="16" width="6" height="5" rx="1" /><rect x="15.5" y="16" width="6" height="5" rx="1" /><path d="M12 8v3M5.5 16v-2.5h13V16" /></svg>),
  DS: (<svg {...svgProps}><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" /><path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" /></svg>),
  AIML: (<svg {...svgProps}><path d="M12 3a3 3 0 0 0-3 3v.5A2.5 2.5 0 0 0 7 9a2.5 2.5 0 0 0 .5 4.9A3 3 0 0 0 12 18a3 3 0 0 0 4.5-4.1A2.5 2.5 0 0 0 17 9a2.5 2.5 0 0 0-2-2.5V6a3 3 0 0 0-3-3Z" /><path d="M12 6v12" /></svg>),
  CV: (<svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M12 3v6M21 12h-6M16.5 18l-3-5.2M7.5 18l3-5.2M3 12h6M9 5l3 5.2" /></svg>),
  MBA: (<svg {...svgProps}><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /><path d="M19 7v3h-3" /></svg>),
  ME: (<svg {...svgProps}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" /></svg>),
};
const leaderIcon = (role) => LEADER_ICONS[(role || "").toLowerCase().trim()] || LEADER_ICONS.secretary;
const coordIcon = (role) => COORD_ICONS[(role || "").split("-")[0].trim().toUpperCase()] || COORD_ICONS.CSE;

/* --- Committee icons --- */
const AvatarIcon = (<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="8" r="4" /><path d="M4 20.5c0-4 3.6-7 8-7s8 3 8 7V21H4z" /></svg>);
const CrownIcon = (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 7.5l4.2 3.5L12 4.5l4.8 6.5L21 7.5 19 19H5z" /></svg>);
const StarIcon = (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l2.6 5.3 5.9.86-4.27 4.16 1 5.88L12 16.9l-5.23 2.3 1-5.88L2.5 9.16l5.9-.86z" /></svg>);
const CalIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="17" rx="2.5" /><path d="M16 2.5v4M8 2.5v4M3 10h18" /></svg>);
const CapIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10 12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.7 2 6 2s6-1 6-2v-5" /></svg>);

/* --- Distinguished Alumni icons --- */
const SearchIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>);
const PeopleIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 6.2a3 3 0 0 1 0 5.6" /><path d="M17 14.4A5.5 5.5 0 0 1 20.5 19" /></svg>);
const ChevronIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>);
const BriefcaseIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="7.5" width="19" height="13" rx="2.5" /><path d="M8 7.5V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1.5" /><path d="M2.5 12.5h19" /></svg>);
const PinIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>);

/* --- Distinguished Alumni helpers --- */
const DEPT_ORDER = ["ISE", "CSE", "ECE", "Civil", "Mechanical", "Bio Technology", "Other"];
const normalizeBranch = (b = "") => {
  const s = b.toUpperCase();
  if (s.includes("ISE")) return "ISE";
  if (s.includes("CSE")) return "CSE";
  if (s.includes("ECE")) return "ECE";
  if (s.includes("CIVIL")) return "Civil";
  if (s.includes("MECH")) return "Mechanical";
  if (s.includes("BIO")) return "Bio Technology";
  return "Other";
};
const initials = (name = "") => {
  const w = name.trim().split(/\s+/);
  return ((w[0]?.[0] || "") + (w[1]?.[0] || "")).toUpperCase() || "A";
};
const parseDesignation = (d = "") => {
  const p = d.split(",").map((s) => s.trim()).filter(Boolean);
  if (p.length >= 3) return { role: p[0], company: p.slice(1, -1).join(", "), location: p[p.length - 1] };
  if (p.length === 2) return { role: p[0], company: p[1], location: "" };
  return { role: p[0] || "", company: "", location: "" };
};
const deriveTags = (d = "") => {
  const s = d.toLowerCase();
  const t = [];
  if (/manager|lead|head|director|president|chief|founder|ceo|cto|\bvp\b/.test(s)) t.push("Leadership");
  if (/bank|financ|account|invest/.test(s)) t.push("Finance");
  else if (/engineer|develop|software|tech|architect|\bdata\b|cloud/.test(s)) t.push("Technology");
  else if (/\bhr\b|human resource|recruit|talent/.test(s)) t.push("Human Resources");
  else if (/consult/.test(s)) t.push("Consulting");
  t.push("Mentor");
  return t.slice(0, 3);
};

/* Floating newsletter button with hover typing-popup */
const NEWS_TITLE = "Alumni Newsletter";
const NEWS_BODY = "Read our latest newsletter for alumni achievements, events, and announcements.";
const NewsIcon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h13a1 1 0 0 1 1 1v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a1 1 0 0 1 1-1Z" /><path d="M18 8h2a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2" /><path d="M7 8h7M7 12h7M7 16h4" /></svg>);

const NewsletterFab = ({ href }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(0);
  // show the popup automatically a moment after load
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!open) { setTyped(0); return; }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setTyped(NEWS_BODY.length); return; }
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(i);
      if (i >= NEWS_BODY.length) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, [open]);
  if (!href) return null;
  return (
    <a
      className="news-fab"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Alumni Newsletter — open PDF"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <div className={`news-fab__pop${open ? " is-open" : ""}`} role="status" aria-hidden={!open}>
        <strong className="news-fab__pop-title">{NEWS_TITLE}</strong>
        <span className="news-fab__pop-text">
          {NEWS_BODY.slice(0, typed)}
          {typed < NEWS_BODY.length && <i className="news-fab__caret" />}
        </span>
      </div>
      <span className="news-fab__btn">
        <span className="news-fab__ping" aria-hidden="true" />
        {NewsIcon}
      </span>
    </a>
  );
};

const Alumni = () => {
  const [data, setData] = useState(alumniFallback);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_DATABASEURL || 'http://localhost:4001/'}api/alumni`)
      .then((res) => {
        if (res?.data?.data) setData(res.data.data);
      })
      .catch((err) => console.log("Using bundled fallback for alumni content:", err?.message))
      .finally(() => setLoading(false));
  }, []);

  const { hero = {}, stats = [], about = {}, formation = {}, committee = {}, newsletter = {}, distinguished = {}, meets = {}, talks = {} } = data || {};
  const [query, setQuery] = useState("");
  const [showCoords, setShowCoords] = useState(false);
  const [showCommittee, setShowCommittee] = useState(false);
  const officeBearers = (formation.officials || []).filter((o) => !/coordinator/i.test(o.role));
  const coordinators = (formation.officials || []).filter((o) => /coordinator/i.test(o.role));
  const COORD_INITIAL = 4;
  const cmFeatured = (committee.members || []).slice(0, 2);
  const cmRest = (committee.members || []).slice(2);

  // Distinguished alumni: search + branch filter + sort, with "view all" reveal
  const [daBranch, setDaBranch] = useState("All");
  const [daSort, setDaSort] = useState("featured");
  const [daBranchOpen, setDaBranchOpen] = useState(false);
  const [daSortOpen, setDaSortOpen] = useState(false);
  const [daShowAll, setDaShowAll] = useState(false);
  const DA_INITIAL = 4;

  const daBranches = useMemo(() => {
    const present = new Set((distinguished.list || []).map((a) => normalizeBranch(a.branch)));
    return DEPT_ORDER.filter((d) => present.has(d));
  }, [distinguished.list]);

  const daFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = (distinguished.list || []).filter((a) => {
      const okQ = !q || [a.name, a.branch, a.designation].some((v) => (v || "").toLowerCase().includes(q));
      const okB = daBranch === "All" || normalizeBranch(a.branch) === daBranch;
      return okQ && okB;
    });
    if (daSort === "name") arr = [...arr].sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [query, daBranch, daSort, distinguished.list]);

  useEffect(() => { setDaShowAll(false); }, [query, daBranch, daSort]);

  if (loading || !data) {
    return (
      <div className="alm-page">
        <Header parentMenu="outreach" topbarEnable="enable" />
        <div className="container" style={{ padding: "8rem 0", textAlign: "center", color: "#53545b" }}>
          {loading ? "Loading…" : "Alumni content is currently unavailable. Please check back later."}
        </div>
        <Footer />
      </div>
    );
  }

  const daVisible = daShowAll ? daFiltered : daFiltered.slice(0, DA_INITIAL);
  const SORT_LABELS = { featured: "Featured", name: "Name (A–Z)" };

  const heroParts = (hero.title || "").trim().split(" ");
  const heroLast = heroParts.pop();

  return (
    <div className="alm-page">
      <Header parentMenu="student-life" topbarEnable="enable" />

      {/* ---- Hero (shared PageBanner) ---- */}
      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.banner ? mediaUrl(hero.banner) : undefined}
      />

      {/* ---- Stats ---- */}
      <div className="alm-stats">
        <div className="container">
          <motion.div className="alm-stats__inner" variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {stats.map((s, i) => (
              <motion.div className="alm-stats__item" key={i} variants={itemV}>
                <span className="alm-stats__num">
                  <CountUp end={s.value} separator="," duration={2.2} enableScrollSpy scrollSpyOnce />
                  {s.suffix && <span>{s.suffix}</span>}
                </span>
                <span className="alm-stats__label">{s.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ---- About ---- */}
      <section className="alm-sec">
        <div className="container">
          <Rise><SectionHead eyebrow={about.eyebrow} heading={about.heading} /></Rise>
          <div className="alm-about">
            <Rise className="alm-about__intro">
              {about.intro.map((p, i) => <p key={i}>{p}</p>)}
            </Rise>
            <motion.div
              className="alm-about__obj"
              variants={containerV}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
            >
              <div className="alm-about__obj-pattern" aria-hidden="true" />
              <h3 className="alm-about__obj-title">{about.objectivesHeading}</h3>
              <ul className="alm-obj">
                {about.objectives.map((o, i) => (
                  <motion.li className="alm-obj__item" key={i} variants={itemV}>
                    <span className="alm-obj__check" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    </span>
                    <span className="alm-obj__text">{o}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- Leadership: office bearers + department coordinators ---- */}
      <section className="alm-sec alm-sec--alt alm-lead">
        <div className="alm-lead__bg" aria-hidden="true">
          <span className="alm-lead__rings" />
          <span className="alm-lead__node" />
          <span className="alm-lead__dots alm-lead__dots--tr" />
          <span className="alm-lead__dots alm-lead__dots--bl" />
        </div>
        <div className="container">
          <Rise>
            <div className="alm-lead__head">
              <span className="alm-lead__eyebrow">
                <span className="alm-lead__eyebrow-line alm-lead__eyebrow-line--l" />
                {formation.eyebrow}
                <span className="alm-lead__eyebrow-line alm-lead__eyebrow-line--r" />
              </span>
              <h2 className="alm-lead__title">{formation.heading}</h2>
              <p className="alm-lead__sub">Meet the office bearers and department coordinators driving alumni engagement and collaboration.</p>
            </div>
          </Rise>

          {/* Office bearers */}
          <motion.div className="alm-bearers" variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            {officeBearers.map((o, i) => (
              <motion.article className="alm-bearer" key={i} variants={itemV}>
                <span className="alm-bearer__badge">
                  <span className="alm-bearer__ring" aria-hidden="true" />
                  {leaderIcon(o.role)}
                </span>
                <span className="alm-bearer__role">{o.role}</span>
                <h3 className="alm-bearer__name">{o.name}</h3>
                <p className="alm-bearer__desig">{o.designation}</p>
                <span className="alm-bearer__rule" aria-hidden="true" />
              </motion.article>
            ))}
          </motion.div>

          {/* Department coordinators */}
          <Rise className="alm-coord-wrap">
            <div className="alm-coord-box">
              <span className="alm-coord-box__label">Department Coordinators</span>
              <motion.div className={`alm-coords${showCoords ? "" : " is-clamped"}`} variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }}>
                {coordinators.map((c, i) => (
                  <motion.div className="alm-coord" key={i} variants={itemV}>
                    <span className="alm-coord__icon">{coordIcon(c.role)}</span>
                    <div className="alm-coord__body">
                      <span className="alm-coord__role">{c.role}</span>
                      <h4 className="alm-coord__name">{c.name}</h4>
                      <p className="alm-coord__desig">{c.designation}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
              {coordinators.length > COORD_INITIAL && (
                <div className="alm-coord-more">
                  <MoreToggle open={showCoords} onClick={() => setShowCoords((v) => !v)} moreLabel={`View all ${coordinators.length} coordinators`} />
                </div>
              )}
            </div>
          </Rise>
        </div>
      </section>

      {/* ---- Committee ---- */}
      <section className="alm-sec alm-cm">
        <div className="container">
          <Rise>
            <div className="alm-cm__head">
              <span className="alm-cm__eyebrow"><i className="alm-cm__dash" />{committee.eyebrow}<i className="alm-cm__dash" /></span>
              <h2 className="alm-cm__title">{committee.heading}</h2>
              <span className="alm-cm__rule" aria-hidden="true" />
              <p className="alm-cm__sub">Meet the alumni leaders and office bearers driving community engagement and collaboration.</p>
            </div>
          </Rise>

          {/* Featured: President + Vice President */}
          <motion.div className="alm-cm-feat" variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            {cmFeatured.map((m, i) => (
              <motion.article className={`alm-fcard${i === 1 ? " alm-fcard--vp" : ""}`} key={i} variants={itemV}>
                <div className="alm-fcard__panel">
                  <span className="alm-fcard__badge">{i === 1 ? StarIcon : CrownIcon}</span>
                  <span className="alm-fcard__avatar">{AvatarIcon}</span>
                </div>
                <div className="alm-fcard__body">
                  <span className="alm-fcard__role">{m.role}</span>
                  <h3 className="alm-fcard__name">{m.name}</h3>
                  <p className="alm-fcard__desig">{m.designation}</p>
                  <div className="alm-chips">
                    <span className="alm-chip">{CalIcon}{m.year}</span>
                    <span className="alm-chip">{CapIcon}{m.department}</span>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          {/* Remaining members */}
          {cmRest.length > 0 && (
            <motion.div className={`alm-cm-rest${showCommittee ? "" : " is-clamped"}`} variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }}>
              {cmRest.map((m, i) => (
                <motion.div className={`alm-mcard${/joint/i.test(m.role) ? " alm-mcard--joint" : ""}`} key={i} variants={itemV}>
                  <span className="alm-mcard__dots" aria-hidden="true" />
                  <span className="alm-mcard__avatar">{AvatarIcon}</span>
                  <div className="alm-mcard__body">
                    <span className="alm-mcard__role">{m.role}</span>
                    <h4 className="alm-mcard__name">{m.name}</h4>
                    <p className="alm-mcard__desig">{m.designation}</p>
                    <div className="alm-chips">
                      <span className="alm-chip">{CalIcon}{m.year}</span>
                      <span className="alm-chip">{CapIcon}{m.department}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
          {cmRest.length > 0 && (
            <div className="alm-cm-more">
              <MoreToggle open={showCommittee} onClick={() => setShowCommittee((v) => !v)} moreLabel={`View all ${committee.members.length} members`} />
            </div>
          )}
        </div>
      </section>

      {/* ---- Distinguished Alumni ---- */}
      <section className="alm-sec alm-sec--alt alm-da">
        <div className="alm-da__bg" aria-hidden="true">
          <span className="alm-da__rings" />
          <span className="alm-da__dots alm-da__dots--tl" />
          <span className="alm-da__dots alm-da__dots--br" />
        </div>
        <div className="container">
          <Rise>
            <div className="alm-da__head">
              <span className="alm-da__eyebrow"><span className="alm-da__eyebrow-line alm-da__eyebrow-line--l" />{distinguished.eyebrow}<span className="alm-da__eyebrow-line alm-da__eyebrow-line--r" /></span>
              <h2 className="alm-da__title">{distinguished.heading}</h2>
              <p className="alm-da__sub">{distinguished.note}</p>
            </div>
          </Rise>

          {/* Toolbar */}
          <Rise className="alm-da__bar-wrap">
            <div className="alm-da__bar">
              <label className="alm-da__search">
                {SearchIcon}
                <input type="text" placeholder="Search by name, branch or company…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search distinguished alumni" />
              </label>
              <span className="alm-da__count">{PeopleIcon}{daFiltered.length} Alumni</span>

              <div className="alm-da__dd">
                <button type="button" className={`alm-da__dd-btn${daBranch !== "All" ? " is-set" : ""}`} onClick={() => { setDaBranchOpen((v) => !v); setDaSortOpen(false); }} aria-expanded={daBranchOpen}>
                  {daBranch === "All" ? "All Branches" : daBranch}<span className="alm-da__dd-chev">{ChevronIcon}</span>
                </button>
                {daBranchOpen && (
                  <div className="alm-da__menu">
                    <button type="button" className={`alm-da__opt${daBranch === "All" ? " is-active" : ""}`} onClick={() => { setDaBranch("All"); setDaBranchOpen(false); }}>All Branches</button>
                    {daBranches.map((b) => (
                      <button type="button" key={b} className={`alm-da__opt${daBranch === b ? " is-active" : ""}`} onClick={() => { setDaBranch(b); setDaBranchOpen(false); }}>{b}</button>
                    ))}
                  </div>
                )}
              </div>

              <div className="alm-da__dd">
                <button type="button" className="alm-da__dd-btn" onClick={() => { setDaSortOpen((v) => !v); setDaBranchOpen(false); }} aria-expanded={daSortOpen}>
                  <span className="alm-da__dd-star">{StarIcon}</span>Sort: {SORT_LABELS[daSort]}<span className="alm-da__dd-chev">{ChevronIcon}</span>
                </button>
                {daSortOpen && (
                  <div className="alm-da__menu">
                    <button type="button" className={`alm-da__opt${daSort === "featured" ? " is-active" : ""}`} onClick={() => { setDaSort("featured"); setDaSortOpen(false); }}>Featured</button>
                    <button type="button" className={`alm-da__opt${daSort === "name" ? " is-active" : ""}`} onClick={() => { setDaSort("name"); setDaSortOpen(false); }}>Name (A–Z)</button>
                  </div>
                )}
              </div>
              {(daBranchOpen || daSortOpen) && <button type="button" className="alm-da__backdrop" aria-label="Close menu" onClick={() => { setDaBranchOpen(false); setDaSortOpen(false); }} />}
            </div>
          </Rise>

          {daFiltered.length === 0 ? (
            <p className="alm-empty">No alumni match your search.</p>
          ) : (
            <Rise>
              <div className={`alm-da__grid${daShowAll ? " is-scroll" : ""}`}>
                {daVisible.map((a, i) => {
                  const d = parseDesignation(a.designation);
                  return (
                    <article className="alm-da-card" key={i}>
                      <span className="alm-da-card__dots" aria-hidden="true" />
                      <span className="alm-da-card__avatar">{initials(a.name)}</span>
                      <h4 className="alm-da-card__name">{a.name}</h4>
                      <span className="alm-da-card__branch">{a.branch}</span>
                      {d.role && <p className="alm-da-card__row">{BriefcaseIcon}<span>{d.role}{d.company && <b>{d.company}</b>}</span></p>}
                      {d.location && <p className="alm-da-card__row">{PinIcon}<span>{d.location}</span></p>}
                    </article>
                  );
                })}
              </div>
            </Rise>
          )}

          {daFiltered.length > DA_INITIAL && (
            <div className="alm-da__viewall">
              <button type="button" className="alm-da__viewall-btn" onClick={() => setDaShowAll((v) => !v)}>
                {PeopleIcon}{daShowAll ? "Show Less" : "View All Alumni"}
                <svg className={`alm-da__viewall-arrow${daShowAll ? " is-up" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </button>
              <p className="alm-da__viewall-note">Explore the achievements of all {daFiltered.length} alumni</p>
            </div>
          )}
        </div>
      </section>

      {/* ---- Alumni Meet gallery ---- */}
      <section className="alm-sec alm-sec--alt">
        <div className="container">
          <Rise><SectionHead eyebrow={meets.eyebrow} heading={meets.heading} /></Rise>
          <Rise><GalleryTabs events={normalizeEvents(meets.events)} /></Rise>
        </div>
      </section>

      {/* ---- Alumni Talks gallery ---- */}
      <section className="alm-sec">
        <div className="container">
          <Rise><SectionHead eyebrow={talks.eyebrow} heading={talks.heading} /></Rise>
          <Rise><GalleryTabs events={normalizeEvents(talks.events)} /></Rise>
        </div>
      </section>

      {newsletter.pdf && <NewsletterFab href={mediaUrl(newsletter.pdf.href)} />}

      <Footer />
    </div>
  );
};

export default Alumni;
